import { useState } from "react";

interface ConfirmRightsAlertProps {
  pickerType: string;
  onAccept?: () => void;
  onOpenModal?: () => void;
}

export function ConfirmRightsAlert(props: ConfirmRightsAlertProps) {
  if (props.pickerType === "stocks") {
    return <PexelsConfirmRightsAlert />;
  }

  if (["hashtag", "tagged-in", "creator"].includes(props.pickerType)) {
    return <IgConfirmRightsAlert onAccept={props.onAccept} />;
  }

  return <></>;
}

function PexelsConfirmRightsAlert() {
  return (
    <div
      data-testid="pexels-confirm-rights-alert"
      className="bg-blue-50 text-blue-600 text-sm px-3 py-2 rounded-lg flex items-center gap-2"
    >
      <i className="fa-regular fa-circle-info shrink-0" />
      <span>
        Check the{" "}
        <a
          href="https://www.pexels.com/license/"
          target="__blank"
          rel="noreferrer"
          className="underline"
        >
          Pexels license
        </a>{" "}
        before saving
      </span>
    </div>
  );
}

function IgConfirmRightsAlert({ onAccept }: { onAccept?: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleConfirmChange = (checked: boolean) => {
    setConfirmed(checked);
    if (checked && onAccept) {
      onAccept();
    }
  };

  return (
    <div
      data-testid="ig-confirm-rights-alert"
      className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg"
    >
      <div className="flex items-start gap-3">
        <i className="fa-regular fa-circle-exclamation text-yellow-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-1">
            <p className="font-inter text-sm font-medium text-yellow-800">
              Confirm rights before saving
            </p>
            <button
              onClick={() => setExpanded(!expanded)}
              className="font-inter text-xs text-yellow-700 underline hover:no-underline whitespace-nowrap flex-shrink-0"
            >
              {expanded ? "Hide details ▲" : "Show details ▼"}
            </button>
          </div>

          {expanded && (
            <div className="mt-3 space-y-3 font-inter text-sm text-yellow-700">
              <p>
                If you save content that belongs to someone else, please ensure
                that you have obtained permission and/or have an appropriate
                license from them, and you provide attribution if appropriate.
              </p>
              <p>
                Per our Terms of Service, you warrant that you have the
                necessary rights, licenses and consents when adding content to
                Sked.
              </p>
            </div>
          )}

          <label className="flex items-start gap-2 mt-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => handleConfirmChange(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-yellow-400 text-yellow-600 focus:ring-2 focus:ring-yellow-500/20 cursor-pointer"
            />
            <span className="font-inter text-sm text-yellow-800 select-none group-hover:text-yellow-900">
              I confirm I have the necessary rights, licenses and consents to
              add this content
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}

// Modal component no longer needed - keeping export for backwards compatibility
export function IgConfirmRightsModal(_props: {
  show: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  // This is now deprecated - the inline expansion pattern replaced it
  // Keeping the export to avoid breaking imports, but it does nothing
  return null;
}
